import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, ShoppingBag, Loader2, Lock, ShieldCheck, Tag, Truck, CreditCard, Wallet, CheckCircle2, Award, Store, X, MapPin, UserCheck, LogIn } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { api, formatApiError } from "../lib/api";
import { variantKey } from "../utils/variant";
import { computeShipping, amountToFreeShipping, zoneNameForProvince, zoneCapsForProvince, codProvinces, codMinAmount, formatProvinceList, COD_DEPOSIT_THRESHOLD, COD_DEPOSIT_PERCENT } from "../utils/shipping";
import { comuniForProvince, comuniListText, allFreeShippingComuni, firstCapForComune, provinceForCap } from "../utils/comuni";
import { bgWithImageStyle } from "../utils/themeBg";
import { trackBeginCheckout, trackPurchase } from "../components/leovera/Analytics";
import { useChat } from "../context/ChatContext";
import { FreeShippingMap } from "../components/leovera/FreeShippingMap";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { isPrAccount, prDiscountPct, prAutoDiscountAmount, prBestOverride, pctLabel } from "../utils/prDiscount";

const money = (n) => `€${(Number(n) || 0).toFixed(2)}`;
const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const StepBadge = ({ n, label, active, done }) => (
  <div className="flex items-center gap-2" data-testid={`checkout-step-${n}`}>
    <span className={`flex items-center justify-center w-7 h-7 rounded-full text-[12px] font-[Outfit] border ${
      active ? "bg-[#C0C0C0] text-[#060A14] border-[#C0C0C0]" : done ? "bg-emerald-500/20 text-emerald-300 border-emerald-400/50" : "border-white/20 text-[#94A3B8]"
    }`}>{n}</span>
    <span className={`text-[10px] tracking-[0.24em] uppercase ${active ? "text-white" : "text-[#94A3B8]"}`}>{label}</span>
  </div>
);

const PaymentTrustBadge = ({ compact }) => (
  <div
    data-testid="payment-trust-badge"
    className={`flex flex-wrap items-center gap-x-5 gap-y-2.5 border border-white/10 rounded-sm bg-[var(--lv-surface,#0A1121)] ${compact ? "px-4 py-3 mt-5" : "px-5 py-4 mt-6"}`}
  >
    <div className="flex items-center gap-2 text-[11px] text-[#E5E4E2]">
      <ShieldCheck className="w-4 h-4 text-emerald-400 shrink-0" />
      <span><b className="text-white tracking-wide">Pagamenti Verificati</b> · Stripe</span>
    </div>
    <div className="flex items-center gap-2 text-[11px] text-[#94A3B8]">
      <Lock className="w-3.5 h-3.5 shrink-0" /> Connessione criptata SSL
    </div>
    <div className="flex items-center gap-2 text-[11px] text-[#94A3B8]">
      <CreditCard className="w-3.5 h-3.5 shrink-0" /> Nessun dato carta sui nostri server
    </div>
  </div>
);

const Field = ({ label, testid, value, onChange, type = "text", placeholder, required, error, wide }) => (
  <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
    <span className="text-[10px] tracking-[0.24em] uppercase text-[#94A3B8]">{label}{required && " *"}</span>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
           data-testid={testid}
           className={`mt-1.5 w-full bg-[var(--lv-surface,#0A1121)] border rounded-sm px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#C0C0C0] placeholder:text-[#94A3B8]/50 ${error ? "border-red-400/60" : "border-white/10"}`} />
    {error && <span className="mt-1 block text-[10px] text-red-300" data-testid={`${testid}-error`}>{error}</span>}
  </label>
);

export default function CheckoutPage() {
  const cart = useCart();
  const { user, getToken, refresh, userApi, login } = useAuth();
  const { settings } = useTheme();
  const chat = useChat();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("stripe"); // stripe | cod
  const [codOrder, setCodOrder] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState("ship"); // ship | pickup
  const [pickupPointId, setPickupPointId] = useState("");
  const [codeInput, setCodeInput] = useState("");
  const [mapOpen, setMapOpen] = useState(false);
  // Inline re-login (triggered from the "sessione scaduta" toast's action
  // button) so a customer never has to leave /checkout — and lose their
  // typed shipping details — just to log back in for the PR discount.
  const [loginDialogOpen, setLoginDialogOpen] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginRememberMe, setLoginRememberMe] = useState(true);
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState("");
  // Which flow opened the re-login dialog, purely to pick the right subtitle
  // copy below ("expired" session vs. "you already have an account" vs. "this
  // code requires login").
  const [loginDialogReason, setLoginDialogReason] = useState("expired");
  // The discount code the customer just tried to apply when it was rejected
  // for requiring a login — re-applied automatically once they sign in, so
  // they never have to retype it.
  const [pendingLoginCode, setPendingLoginCode] = useState("");
  // True only while the PR discount disappeared specifically because the
  // session expired (not because an admin revoked the tag) — keeps a
  // persistent "Accedi di nuovo" reachable even after the toast's own action
  // button is dismissed/missed, so the customer is never stuck leaving
  // /checkout (and its typed shipping form) just to log back in.
  const [prExpiredNotice, setPrExpiredNotice] = useState(false);
  const mapComuni = useMemo(() => allFreeShippingComuni(settings), [settings]);
  const mapCapFor = useCallback((name) => firstCapForComune(name, settings), [settings]);

  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", phone: "",
    address: "", city: "", postal: "", province: "",
  });
  const setF = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));
  const pickupPoints = settings?.commerce?.pickup_points || [];
  const selectedPickupPoint = pickupPoints.find((p) => p.name === pickupPointId) || null;

  // Precompila provincia (e città/CAP se il comune è già stato confermato nel carrello)
  // dalla scelta fatta nel CartDrawer, senza sovrascrivere dati già inseriti dal cliente.
  useEffect(() => {
    if (!cart.province) return;
    setForm((f) => {
      if (f.province) return f;
      const province = cart.province.trim().toUpperCase();
      const next = { ...f, province };
      if (cart.comuneConfirmed) {
        if (province === "SR") { next.city = f.city || "Siracusa"; next.postal = f.postal || "96100"; }
        else if (province === "CT") { next.city = f.city || "Catania"; }
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const codList = codProvinces(settings);
  const codMin = codMinAmount(settings);
  const provinceOk = deliveryMethod === "pickup" || codList.includes(form.province.trim().toUpperCase());
  useEffect(() => {
    if (!provinceOk && paymentMethod === "cod") setPaymentMethod("stripe");
  }, [provinceOk, paymentMethod]);
  useEffect(() => {
    if (deliveryMethod === "ship") setPickupPointId("");
    else setForm((f) => ({ ...f, address: "", city: "", postal: "", province: "" }));
  }, [deliveryMethod]);

  // Ensure the cart drawer (global Radix Dialog) is closed on arrival.
  useEffect(() => { cart.setOpen(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Session revalidation: the cached `user` in AuthContext can be stale (set
  // once at app load/login and never re-checked) — if the token expired or
  // the account's role_tag changed since then, re-validating here as soon as
  // the customer reaches checkout keeps the PR discount preview honest
  // instead of showing a hypothetical value the backend will no longer grant.
  const notifyPrStatusChange = (freshUser, prevEmail) => {
    // freshUser === false → token invalid/expired; an object → still a
    // valid session, just not (or no longer) a PR account. Different causes
    // need different copy — and only the "expired" case gets a one-click
    // "Accedi di nuovo" action, since re-logging in wouldn't restore a tag an
    // admin actually removed.
    if (freshUser) {
      setPrExpiredNotice(false);
      toast.error("Il tuo account non ha più lo sconto Cliente PR attivo.", { id: "pr-session-status", position: "top-center" });
    } else {
      setPrExpiredNotice(true);
      setLoginEmail(prevEmail || "");
      toast.error("La tua sessione è scaduta: accedi di nuovo per lo sconto Cliente PR.", {
        id: "pr-session-status",
        position: "top-center",
        action: { label: "Accedi di nuovo", onClick: () => { setLoginDialogReason("expired"); setLoginDialogOpen(true); } },
      });
    }
  };

  // Shared by both desktop/mobile SummaryRows renders. If the code is rejected
  // for requiring a login, keep it typed in the input and open the inline
  // login dialog instead of just showing an error — the customer can sign in
  // without losing their place, and the code is auto-retried (handleInlineLogin).
  const applyDiscountCode = async () => {
    const value = codeInput;
    const res = await cart.addDiscountCode(value);
    if (!res?.valid && res?.login_required) {
      setPendingLoginCode(value.trim().toUpperCase());
      setLoginDialogReason("discount_login_required");
      setLoginDialogOpen(true);
    } else {
      setCodeInput("");
    }
  };

  const handleInlineLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginBusy(true);
    const r = await login(loginEmail, loginPassword, loginRememberMe);
    setLoginBusy(false);
    if (r.ok) {
      setLoginDialogOpen(false);
      setLoginPassword("");
      setPrExpiredNotice(false);
      // Use a DIFFERENT id for the success toast — sonner's dismiss() only
      // flags the old toast for removal, it doesn't clear it, so reusing the
      // same id merges the old `action` button back in and the (already
      // dismissed) toast vanishes almost immediately either way.
      toast.dismiss("pr-session-status");
      const nowPr = (r.user?.role_tag || "cliente") === "pr";
      toast.success(nowPr ? "Accesso effettuato — sconto Cliente PR applicato." : "Accesso effettuato.", { id: "pr-relogin-success", position: "top-center" });
      if (pendingLoginCode) {
        const code = pendingLoginCode;
        setPendingLoginCode("");
        // `cart.addDiscountCode` reads the auth token fresh from storage at call-time
        // (not from a closure), so it already sees this just-completed login correctly.
        const res = await cart.addDiscountCode(code);
        if (res?.valid) setCodeInput("");
      }
    } else {
      setLoginError(r.error);
    }
  };

  useEffect(() => {
    if (!user) return;
    const wasPr = (user.role_tag || "cliente") === "pr";
    const prevEmail = user.email || "";
    refresh().then(({ user: fresh }) => {
      if (fresh === undefined) return; // transient check failure, inconclusive
      const stillPr = !!fresh && (fresh.role_tag || "cliente") === "pr";
      if (wasPr && !stillPr) notifyPrStatusChange(fresh, prevEmail);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Loyalty points preview: fetch this customer's real earning ratio (global
  // or per-user override) so the "+N punti Cashback" shown below mirrors
  // exactly resolve_loyalty_points() server-side, never a guessed number.
  const [loyaltyRatio, setLoyaltyRatio] = useState({ points: 2, eur: 10 });
  useEffect(() => {
    if (!user?.id) return;
    userApi.get("/auth/loyalty")
      .then(({ data }) => {
        if (data?.ratio_eur > 0) setLoyaltyRatio({ points: data.ratio_points, eur: data.ratio_eur });
      })
      .catch(() => {});
  }, [user?.id, userApi]);

  // Guest checkout: if the email just typed already belongs to an account,
  // nudge the customer to log in (so PR discount/loyalty actually apply)
  // instead of silently checking out as a guest. Debounced so it fires once
  // typing settles, not on every keystroke; never runs once logged in.
  const [existingAccountNotice, setExistingAccountNotice] = useState(false);
  useEffect(() => {
    if (user) { setExistingAccountNotice(false); return; }
    const email = form.email.trim();
    if (!emailRe.test(email)) { setExistingAccountNotice(false); return; }
    const t = setTimeout(() => {
      api.post("/auth/email-has-account", { email })
        .then(({ data }) => setExistingAccountNotice(!!data?.exists))
        .catch(() => {}); // transient failure: never block/annoy a guest over this
    }, 600);
    return () => clearTimeout(t);
  }, [form.email, user]);

  const items = cart.items;
  const subtotal = useMemo(() => items.reduce((a, i) => a + i.price * i.quantity, 0), [items]);
  const isPr = isPrAccount(user);

  // Lets the applied discount-code chips/total correctly reflect whether the
  // automatic welcome discount would conflict with them (e.g. it's "esclusivo"),
  // exactly like the server's unified candidate pool — see CartContext.runValidateAll.
  // PR accounts never get it at checkout (server's PR branch never calls
  // compute_welcome_discount at all), so it must never be fetched/merged for them —
  // otherwise a PR who also happens to be a first-time buyer could see a typed code
  // get needlessly dropped here for a welcome conflict the server will never apply.
  // Debounced; only fires once there's a plausible email to check eligibility against.
  useEffect(() => {
    const email = form.email.trim();
    if (isPr || !emailRe.test(email)) { cart.clearWelcomePreview(); return; }
    const t = setTimeout(() => {
      cart.refreshWelcomePreview({ email, phone: form.phone.trim(), name: form.name.trim() });
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.email, form.phone, form.name, subtotal, isPr]);

  const prPct = prDiscountPct(user);
  const prAutoAmount = prAutoDiscountAmount(user, subtotal);
  // PR accounts never stack multiple codes — only the single BEST admin-flagged
  // "pr_override" code (if any) can beat the automatic discount, mirroring the backend.
  const bestOverride = isPr ? prBestOverride(cart.discounts, subtotal) : null;
  const prOverrideActive = isPr && !!bestOverride && bestOverride.amount > prAutoAmount;
  const discountAmount = isPr
    ? Math.min(prOverrideActive ? bestOverride.amount : prAutoAmount, subtotal)
    : Math.min((cart.discountValid ? cart.discountAmount : 0) + cart.welcomeAppliedAmount, subtotal);
  const afterDiscount = Math.max(0, Math.round((subtotal - discountAmount) * 100) / 100);
  const freeByDiscount = isPr ? (prOverrideActive && bestOverride.free_shipping) : (cart.discountValid && cart.discountFreeShipping);
  const hasExclusiveCode = cart.discounts.some((d) => d?.valid && d.stack_mode === "exclusive");
  // Compared as a plain YYYY-MM-DD string in Italy's local date (not the browser's/UTC)
  // so it matches the backend's Europe/Rome check exactly.
  const todayInItaly = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Rome" }).format(new Date());
  const welcomeDiscountEnabled = settings?.commerce?.welcome_discount_enabled !== false
    && !(settings?.commerce?.welcome_discount_valid_until && settings.commerce.welcome_discount_valid_until < todayInItaly);
  const welcomeDiscountPct = Number(settings?.commerce?.welcome_discount_pct ?? 10);
  const isPickup = deliveryMethod === "pickup";
  const shipping = isPickup ? 0 : computeShipping(afterDiscount, settings, freeByDiscount, form.province, form.postal);
  const toFree = isPickup ? 0 : amountToFreeShipping(afterDiscount, settings);
  const zoneName = isPickup ? "" : zoneNameForProvince(form.province, settings, form.postal);
  const total = Math.max(0, Math.round((afterDiscount + shipping) * 100) / 100);
  const amountOk = codMin <= 0 || total >= codMin;
  const codEligible = provinceOk && amountOk;
  const codNeedsDeposit = total > COD_DEPOSIT_THRESHOLD;
  const depositAmount = Math.round(total * COD_DEPOSIT_PERCENT * 100) / 100;
  const cashDueAfterDeposit = Math.max(0, Math.round((total - depositAmount) * 100) / 100);
  // Mirrors resolve_loyalty_points(db, user, amount_eur) server-side exactly:
  // int(amount_eur / ratio_eur) * ratio_points. Guests never earn points.
  const estimatedPoints = user ? Math.floor(total / loyaltyRatio.eur) * loyaltyRatio.points : 0;

  const errors = {
    name: !form.name.trim() ? "Inserisci il nome" : "",
    email: !emailRe.test(form.email.trim()) ? "Email non valida" : "",
    address: !isPickup && !form.address.trim() ? "Inserisci l'indirizzo" : "",
    city: !isPickup && !form.city.trim() ? "Inserisci la città" : "",
    postal: !isPickup && !/^\d{4,5}$/.test(form.postal.trim()) ? "CAP non valido" : "",
    province: !isPickup && !/^[A-Za-z]{2}$/.test(form.province.trim()) ? "Sigla (2 lettere)" : "",
    pickup_point: isPickup && !pickupPointId ? "Seleziona un punto di ritiro" : "",
  };
  const formValid = Object.values(errors).every((e) => !e);

  if (items.length === 0 && step !== 3) {
    return (
      <div className="min-h-screen text-white flex items-center justify-center px-4" style={bgWithImageStyle()} data-testid="checkout-empty">
        <div className="text-center max-w-md">
          <ShoppingBag className="w-10 h-10 text-[#C0C0C0] mx-auto mb-6" strokeWidth={1.5} />
          <h1 className="font-[Outfit] text-3xl font-light">Il carrello è vuoto</h1>
          <p className="mt-3 text-[#94A3B8] text-sm">Aggiungi qualche prodotto per procedere al checkout.</p>
          <Link to="/" data-testid="checkout-empty-shop"
                className="mt-8 inline-flex items-center gap-3 bg-[#C0C0C0] text-[#060A14] px-8 py-4 rounded-sm text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-white transition-colors">
            <ArrowLeft className="w-4 h-4" /> Torna allo Shop
          </Link>
        </div>
      </div>
    );
  }

  const pay = async () => {
    setTouched(true);
    if (!formValid) { toast.error("Completa i dati di spedizione"); return; }
    setLoading(true);
    try {
      // Re-check the session right before charging: if it expired between
      // page-load and this click, never let a stale PR preview go through —
      // stop and ask the customer to log in again instead of silently
      // charging the full (non-PR) price after promising a 25% discount.
      // A transient check failure (network/5xx/429) is inconclusive and must
      // NOT downgrade the session — proceed with whatever token we already have.
      const wasPr = isPr;
      const prevEmail = user?.email || "";
      const { user: freshUser } = await refresh();
      if (freshUser !== undefined) {
        const freshIsPr = !!freshUser && (freshUser.role_tag || "cliente") === "pr";
        if (wasPr && !freshIsPr) {
          notifyPrStatusChange(freshUser, prevEmail);
          setLoading(false);
          return;
        }
      }
      // Attach the LOGGED-IN customer's JWT (if any) so the backend's
      // get_optional_user can resolve the real account server-side — this is
      // what actually drives the automatic PR 25% discount and loyalty points
      // award on checkout (never trusted from payload.user_id).
      const authToken = getToken && getToken();
      const authHeaders = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const payload = {
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          variant: variantKey(i.flavor, i.size),
          bundle_id: i.bundle_id || undefined,
        })),
        origin_url: window.location.origin,
        customer_name: form.name.trim(),
        customer_email: form.email.trim(),
        customer_phone: form.phone.trim(),
        shipping_address: isPickup ? "" : form.address.trim(),
        shipping_city: isPickup ? "" : form.city.trim(),
        shipping_postal_code: isPickup ? "" : form.postal.trim(),
        shipping_province: isPickup ? "" : form.province.trim().toUpperCase(),
        delivery_method: deliveryMethod,
      };
      if (isPickup) {
        payload.pickup_point_id = pickupPointId;
        payload.pickup_point_name = selectedPickupPoint?.name || pickupPointId;
      }
      if (cart.discountCodes.length) payload.discount_codes = cart.discountCodes;

      if (paymentMethod === "cod" && codEligible) {
        if (codNeedsDeposit) {
          const { data } = await api.post("/checkout/cod-deposit", payload, { headers: authHeaders });
          if (data?.url) {
            window.location.href = data.url; // Stripe hosted checkout — deposit only
          } else {
            toast.error("Impossibile avviare il pagamento dell'anticipo");
            setLoading(false);
          }
          return;
        }
        const { data } = await api.post("/checkout/cod", payload, { headers: authHeaders });
        trackPurchase({ order_number: data.order_number, total: data.total, items: items.map((i) => ({ product_id: i.product_id, product_name: i.name, quantity: i.quantity, unit_price: i.price })) });
        setCodOrder(data);
        cart.clear();
        setStep(3);
        setLoading(false);
        return;
      }

      const { data } = await api.post("/checkout/cart", payload, { headers: authHeaders });
      if (data?.url) {
        window.location.href = data.url; // Stripe hosted checkout
      } else {
        toast.error("Impossibile avviare il pagamento");
        setLoading(false);
      }
    } catch (e) {
      toast.error(formatApiError(e));
      setLoading(false);
    }
  };

  const SummaryRows = () => (
    <dl className="space-y-2 text-sm">
      {isPr && !prOverrideActive && discountAmount > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 mb-1 rounded-sm bg-emerald-500/10 border border-emerald-400/30 text-emerald-200 text-[11px]" data-testid="checkout-pr-logged-badge">
          <UserCheck className="w-3.5 h-3.5 shrink-0" />
          <span>Loggato come <strong className="font-medium">{user.name || user.email}</strong>, PR <strong>-{pctLabel(prPct)}%</strong> applicato</span>
        </div>
      )}
      {!user && prExpiredNotice && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 mb-1 rounded-sm bg-amber-500/10 border border-amber-400/30 text-amber-200 text-[11px]" data-testid="checkout-pr-relogin-notice">
          <span>Sessione scaduta: lo sconto Cliente PR non è più applicato.</span>
          <button type="button" onClick={() => { setLoginDialogReason("expired"); setLoginDialogOpen(true); }} data-testid="checkout-reopen-login"
                  className="underline underline-offset-2 shrink-0 hover:text-white">
            Accedi di nuovo
          </button>
        </div>
      )}
      <div className="flex items-center justify-between text-[#94A3B8]">
        <dt>Subtotale</dt>
        <dd className="font-[Outfit] text-[#E5E4E2]" data-testid="checkout-subtotal">{money(subtotal)}</dd>
      </div>

      <div className="py-1">
        {!isPr && !hasExclusiveCode && welcomeDiscountEnabled && (
          <p className="text-[10px] text-[#94A3B8] leading-relaxed mb-2" data-testid="welcome-discount-note">
            <Award className="w-3 h-3 inline-block mr-1 -mt-0.5 text-emerald-300" />
            Primo ordine? Lo sconto benvenuto del {welcomeDiscountPct}% viene applicato automaticamente.
          </p>
        )}
        <div className="flex items-stretch gap-2">
          <div className="relative flex-1">
            <Tag className="w-3.5 h-3.5 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={codeInput}
              onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === "Enter") applyDiscountCode(); }}
              placeholder="Codice sconto"
              data-testid="checkout-discount-input"
              className="w-full bg-[#0A1121] border border-white/10 rounded-sm pl-9 pr-3 py-2 text-white text-[11px] uppercase tracking-[0.14em] focus:outline-none focus:border-[#C0C0C0] placeholder:text-[#94A3B8]/60"
            />
          </div>
          <button onClick={applyDiscountCode} disabled={cart.discountLoading || !codeInput.trim()} data-testid="checkout-discount-apply"
                  className="px-3 rounded-sm text-[10px] tracking-[0.18em] uppercase bg-[#C0C0C0] text-[#060A14] font-medium hover:bg-white disabled:opacity-40">
            {cart.discountLoading ? "…" : "Applica"}
          </button>
        </div>
        {cart.discountError && (
          <p className="mt-1.5 text-[10px] text-red-300 flex items-center gap-2 flex-wrap" data-testid="checkout-discount-error">
            <span>{cart.discountError.message}</span>
            {cart.discountError.login_required && (
              <button type="button"
                      onClick={() => { setLoginDialogReason("discount_login_required"); setLoginDialogOpen(true); }}
                      data-testid="checkout-discount-login-btn"
                      className="underline underline-offset-2 hover:text-white font-medium text-red-200">
                Accedi o registrati
              </button>
            )}
          </p>
        )}
        {cart.discountCodes.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="checkout-discount-chips">
            {cart.discountCodes.map((code) => {
              const d = cart.discounts.find((x) => x?.code === code);
              const suppressed = d?.suppressed_by_welcome;
              return (
              <span key={code} data-testid={`checkout-discount-chip-${code}`}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[10px] tracking-[0.1em] ${suppressed ? "bg-white/5 border-white/15 text-[#94A3B8]" : "bg-emerald-500/10 border-emerald-400/30 text-emerald-200"}`}>
                {code}
                {suppressed && (
                  <span data-testid={`checkout-discount-chip-suppressed-${code}`}
                        className="px-1 py-px rounded-full text-[8px] tracking-[0.12em] uppercase bg-white/10 border border-white/20 text-[#C0C0C0]">
                    In pausa · benvenuto attivo
                  </span>
                )}
                {!suppressed && d && d.valid && d.stack_mode === "exclusive" && (
                  <span data-testid={`checkout-discount-chip-exclusive-${code}`}
                        className="px-1 py-px rounded-full text-[8px] tracking-[0.12em] uppercase bg-amber-500/15 border border-amber-400/30 text-amber-200">
                    Esclusivo
                  </span>
                )}
                <button onClick={() => cart.removeDiscountCode(code)} data-testid={`checkout-discount-chip-remove-${code}`}
                        className="hover:text-white" aria-label={`Rimuovi codice ${code}`}>
                  <X className="w-3 h-3" />
                </button>
              </span>
              );
            })}
          </div>

        )}
      </div>

      {discountAmount > 0 && (
        <div className="flex items-center justify-between text-emerald-300" data-testid="checkout-discount-row">
          <dt className="inline-flex items-center gap-1.5">
            <Tag className="w-3.5 h-3.5" />
            {isPr
              ? (prOverrideActive ? `Sconto PR speciale (${bestOverride.code})` : `Sconto PR a vita (${pctLabel(prPct)}%)`)
              : `Sconto (${[...cart.discountCodes.filter((c) => !cart.discounts.find((x) => x?.code === c)?.suppressed_by_welcome), ...(cart.welcomeAppliedAmount > 0 ? ["Benvenuto primo ordine"] : [])].join(", ")})`}
          </dt>
          <dd className="font-[Outfit]">−{money(discountAmount)}</dd>
        </div>
      )}
      {isPr && cart.discountCodes.length > 0 && !prOverrideActive && (
        <p className="text-[10px] text-amber-300" data-testid="checkout-pr-code-note">
          Sei un account PR: vale solo lo sconto automatico del {pctLabel(prPct)}%, non cumulabile con i codici sconto.
        </p>
      )}
      <div className="flex items-center justify-between text-[#94A3B8]">
        <dt>{isPickup ? "Ritiro in punto convenzionato" : <>Spedizione{zoneName ? <span className="text-[10px] text-[#94A3B8]/80"> ({zoneName})</span> : ""}</>}</dt>
        <dd className={`font-[Outfit] ${shipping === 0 ? "text-emerald-300" : "text-[#E5E4E2]"}`} data-testid="checkout-shipping">
          {isPickup ? "Gratuito" : (shipping === 0 ? "Gratuita" : money(shipping))}
        </dd>
      </div>
      <div className="flex items-center justify-between pt-3 mt-1 border-t border-white/10">
        <dt className="text-white tracking-[0.16em] uppercase text-[12px]">Totale</dt>
        <dd className="font-[Outfit] text-white text-2xl" data-testid="checkout-total">{money(total)}</dd>
      </div>
      {user && estimatedPoints > 0 && (
        <div className="flex items-center justify-between text-emerald-300 pt-1" data-testid="checkout-loyalty-preview">
          <dt className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em]">
            <Award className="w-3 h-3" /> Punti Cashback stimati
          </dt>
          <dd className="font-[Outfit] text-[12px]">fino a +{estimatedPoints}</dd>
        </div>
      )}
    </dl>
  );

  return (
    <div className="min-h-screen text-white" style={bgWithImageStyle()} data-testid="checkout-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <button onClick={() => navigate(-1)} data-testid="checkout-back"
                className={`inline-flex items-center gap-2 text-[10px] tracking-[0.24em] uppercase text-[#94A3B8] hover:text-white mb-8 ${step === 3 ? "invisible" : ""}`}>
          <ArrowLeft className="w-3.5 h-3.5" /> Continua lo shopping
        </button>

        {step !== 3 && (
          <div className="flex items-center gap-4 sm:gap-8 mb-10">
            <StepBadge n={1} label="Riepilogo" active={step === 1} done={step > 1} />
            <span className="flex-1 h-px bg-white/10" />
            <StepBadge n={2} label="Dati & Pagamento" active={step === 2} done={false} />
          </div>
        )}

        <span className="text-[10px] tracking-[0.4em] uppercase text-[#C0C0C0]">LEOVERA Nutrition</span>

        {step === 1 && (
          <div data-testid="checkout-summary-step" className="animate-in fade-in duration-500">
            <h1 className="mt-2 font-[Outfit] text-4xl sm:text-5xl font-light tracking-tight">Riepilogo ordine</h1>
            <div className="mt-8 divide-y divide-white/5 border-y border-white/10">
              {items.map((it) => (
                <div key={it.variant_key} className="flex items-center gap-4 py-4" data-testid={`checkout-item-${it.product_id}`}>
                  <div className="w-16 h-16 rounded-sm bg-[var(--lv-surface,#0A1121)] border border-white/10 overflow-hidden shrink-0">
                    {it.image && <img src={it.image} alt={it.name} className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-medium truncate">{it.name}</p>
                    {(it.flavor || it.size) && (
                      <p className="text-[11px] text-[#94A3B8] mt-0.5 tracking-wide uppercase">
                        {[it.flavor, it.size].filter(Boolean).join(" · ")}
                      </p>
                    )}
                    <p className="text-[11px] text-[#94A3B8] mt-0.5 font-[Outfit]">{money(it.price)} × {it.quantity}</p>
                  </div>
                  <div className="font-[Outfit] text-white text-right shrink-0" data-testid={`checkout-line-total-${it.product_id}`}>
                    {money(it.price * it.quantity)}
                  </div>
                </div>
              ))}
            </div>

            {shipping > 0 && toFree > 0 && (
              <div className="mt-4 flex items-center gap-2 text-[11px] text-[#C0C0C0] bg-[var(--lv-surface,#0A1121)] border border-white/10 rounded-sm px-3 py-2.5" data-testid="checkout-free-ship-hint">
                <Truck className="w-4 h-4" /> Ti mancano <b className="font-[Outfit] text-white">{money(toFree)}</b> alla spedizione gratuita
              </div>
            )}

            <div className="mt-6">{SummaryRows()}</div>

            <PaymentTrustBadge compact />

            <button onClick={() => { trackBeginCheckout(items, total); setStep(2); }} data-testid="checkout-to-payment"
                    className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-[#C0C0C0] text-[#060A14] px-8 py-4 rounded-sm text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-white transition-colors">
              Procedi al pagamento <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {step === 2 && (
          <div data-testid="checkout-payment-step" className="animate-in fade-in duration-500">
            <h1 className="mt-2 font-[Outfit] text-4xl sm:text-5xl font-light tracking-tight">Dati e pagamento</h1>

            <div className="mt-8">
              <h2 className="text-[10px] tracking-[0.28em] uppercase text-[#C0C0C0] mb-4">Metodo di consegna</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="delivery-method-selector">
                <button type="button" onClick={() => setDeliveryMethod("ship")} data-testid="delivery-method-ship"
                        className={`flex items-center gap-3 px-4 py-3.5 rounded-sm border text-left transition-colors ${
                          deliveryMethod === "ship" ? "border-[#C0C0C0] bg-[#C0C0C0]/10" : "border-white/10 hover:border-white/25"
                        }`}>
                  <Truck className="w-4 h-4 text-[#C0C0C0] shrink-0" />
                  <span>
                    <span className="block text-[12px] text-white">Spedizione</span>
                    <span className="block text-[10px] text-[#94A3B8]">Consegna all'indirizzo che preferisci</span>
                  </span>
                </button>
                {pickupPoints.length > 0 && (
                  <button type="button" onClick={() => setDeliveryMethod("pickup")} data-testid="delivery-method-pickup"
                          className={`flex items-center gap-3 px-4 py-3.5 rounded-sm border text-left transition-colors ${
                            deliveryMethod === "pickup" ? "border-emerald-400/60 bg-emerald-500/10" : "border-white/10 hover:border-white/25"
                          }`}>
                    <Store className="w-4 h-4 text-emerald-300 shrink-0" />
                    <span>
                      <span className="block text-[12px] text-white">Ritiro in punto convenzionato</span>
                      <span className="block text-[10px] text-[#94A3B8]">Gratuito · scegli dove ritirare</span>
                    </span>
                  </button>
                )}
              </div>
            </div>

            <div className="mt-8">
              <h2 className="text-[10px] tracking-[0.28em] uppercase text-[#C0C0C0] mb-4">{isPickup ? "Dati di contatto" : "Dati di spedizione"}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Nome e cognome" testid="checkout-name" value={form.name} onChange={setF("name")} required error={touched ? errors.name : ""} wide />
                <Field label="Email" testid="checkout-email" type="email" value={form.email} onChange={setF("email")} required error={touched ? errors.email : ""} />
                <Field label="Telefono" testid="checkout-phone" value={form.phone} onChange={setF("phone")} placeholder="Opzionale" />
                {!isPickup && (
                  <>
                    <Field label="Indirizzo" testid="checkout-address" value={form.address} onChange={setF("address")} placeholder="Via, numero civico" required error={touched ? errors.address : ""} wide />
                    <Field label="Città" testid="checkout-city" value={form.city} onChange={setF("city")} required error={touched ? errors.city : ""} />
                    <Field label="CAP" testid="checkout-postal" value={form.postal} onChange={setF("postal")} required error={touched ? errors.postal : ""} />
                    <Field label="Provincia" testid="checkout-province" value={form.province} onChange={(v) => setF("province")(v.toUpperCase().slice(0, 2))} placeholder="Es. SR" required error={touched ? errors.province : ""} />
                  </>
                )}
              </div>
              {!isPickup && ["SR", "CT"].includes(form.province.trim().toUpperCase()) && (() => {
                const comuni = comuniForProvince(form.province, settings);
                if (!comuni.length) return null;
                return (
                  <p className="mt-3 text-[11px] text-[#94A3B8]" data-testid="checkout-comune-note">
                    Spedizione gratuita solo per {comuni.length === 1 ? "il comune di" : "i comuni di"} {comuniListText(zoneCapsForProvince(form.province, settings), settings)}.
                  </p>
                );
              })()}
              {!isPickup && (
                <button
                  type="button"
                  onClick={() => setMapOpen(true)}
                  data-testid="checkout-map-trigger"
                  className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#C0C0C0] hover:text-white underline underline-offset-2 transition-colors"
                >
                  <MapPin className="w-3 h-3" />
                  Vedi la mappa dei comuni con spedizione gratuita
                </button>
              )}
              {isPickup && (
                <div className="mt-6">
                  <h3 className="text-[10px] tracking-[0.28em] uppercase text-[#94A3B8] mb-3">Scegli il punto di ritiro *</h3>
                  <div className="space-y-2" data-testid="pickup-point-list">
                    {pickupPoints.map((p, idx) => (
                      <button key={`${p.name}-${idx}`} type="button" onClick={() => setPickupPointId(p.name)}
                              data-testid={`pickup-point-${idx}`}
                              className={`w-full text-left px-4 py-3 rounded-sm border transition-colors ${
                                pickupPointId === p.name ? "border-emerald-400/60 bg-emerald-500/10" : "border-white/10 hover:border-white/25"
                              }`}>
                        <span className="block text-[13px] text-white">{p.name}</span>
                        {p.address && <span className="block text-[11px] text-[#94A3B8] mt-0.5">{p.address}</span>}
                        {p.hours && <span className="block text-[10px] text-[#94A3B8]/80 mt-0.5">{p.hours}</span>}
                      </button>
                    ))}
                  </div>
                  {touched && errors.pickup_point && (
                    <span className="mt-2 block text-[10px] text-red-300" data-testid="pickup-point-error">{errors.pickup_point}</span>
                  )}
                </div>
              )}
            </div>

            <div className="mt-8">
              <h2 className="text-[10px] tracking-[0.28em] uppercase text-[#C0C0C0] mb-4">Metodo di pagamento</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="payment-method-selector">
                <button type="button" onClick={() => setPaymentMethod("stripe")} data-testid="payment-method-stripe"
                        className={`flex items-center gap-3 px-4 py-3.5 rounded-sm border text-left transition-colors ${
                          paymentMethod === "stripe" ? "border-[#C0C0C0] bg-[#C0C0C0]/10" : "border-white/10 hover:border-white/25"
                        }`}>
                  <CreditCard className="w-4 h-4 text-[#C0C0C0] shrink-0" />
                  <span>
                    <span className="block text-[12px] text-white">Carta (Stripe)</span>
                    <span className="block text-[10px] text-[#94A3B8]">Pagamento online sicuro</span>
                  </span>
                </button>
                {codEligible && (
                  <button type="button" onClick={() => setPaymentMethod("cod")} data-testid="payment-method-cod"
                          className={`flex items-center gap-3 px-4 py-3.5 rounded-sm border text-left transition-colors ${
                            paymentMethod === "cod" ? "border-emerald-400/60 bg-emerald-500/10" : "border-white/10 hover:border-white/25"
                          }`}>
                    <Wallet className="w-4 h-4 text-emerald-300 shrink-0" />
                    <span>
                      <span className="block text-[12px] text-white">{isPickup ? "Paga al ritiro" : "Pagamento alla consegna"}</span>
                      <span className="block text-[10px] text-[#94A3B8]">{isPickup ? "Contanti al punto di ritiro" : "Contanti al corriere"}</span>
                    </span>
                  </button>
                )}
              </div>
              {!codEligible && (
                <p className="mt-2 text-[10px] text-[#94A3B8]" data-testid="payment-method-cod-hint">
                  {!provinceOk
                    ? `Il pagamento alla consegna è disponibile solo per le provincie di ${formatProvinceList(codList) || "—"}.`
                    : `Il pagamento alla consegna richiede un ordine minimo di ${money(codMin)}.`}
                </p>
              )}
            </div>

            <div className="mt-8 bg-[var(--lv-surface,#0A1121)] border border-white/10 rounded-md p-6">
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#94A3B8] mb-4">Riepilogo</p>
              {SummaryRows()}
            </div>

            {paymentMethod === "cod" ? (
              codNeedsDeposit ? (
                <div className="mt-6 border border-amber-400/30 bg-amber-500/5 rounded-sm px-4 py-4 text-[11px] text-[#E5E4E2] leading-relaxed" data-testid="cod-deposit-notice">
                  <p>
                    Il tuo ordine supera i {money(COD_DEPOSIT_THRESHOLD)}: per confermare {isPickup ? "il pagamento al ritiro" : "il contrassegno"} è richiesto
                    un anticipo del {Math.round(COD_DEPOSIT_PERCENT * 100)}% (<b className="text-white">{money(depositAmount)}</b>) da
                    pagare ora con carta. Il resto (<b className="text-white">{money(cashDueAfterDeposit)}</b>) lo paghi in contanti {isPickup ? "al ritiro" : "alla consegna"}.
                  </p>
                  <button type="button"
                          onClick={() => chat.openWith("Il mio ordine supera i 300€: vorrei un preventivo personalizzato per il pagamento alla consegna.")}
                          data-testid="cod-deposit-contact-staff"
                          className="mt-3 inline-flex items-center gap-1.5 text-emerald-300 hover:text-emerald-200 underline underline-offset-2">
                    Oppure contatta lo staff per un preventivo personalizzato
                  </button>
                </div>
              ) : (
                <div className="mt-6 flex items-center gap-3 text-[11px] text-[#94A3B8]" data-testid="cod-notice">
                  <Wallet className="w-4 h-4 text-emerald-300" /> {isPickup
                    ? "Pagherai in contanti direttamente al ritiro presso il punto scelto · nessun pagamento online richiesto ora"
                    : "Pagherai in contanti direttamente al corriere alla consegna · nessun pagamento online richiesto ora"}
                </div>
              )
            ) : (
              <>
                <div className="mt-6 flex items-center gap-3 text-[11px] text-[#94A3B8]">
                  <ShieldCheck className="w-4 h-4 text-emerald-400" /> Pagamento sicuro gestito da Stripe · nessun dato della carta transita dai nostri server
                </div>
                <PaymentTrustBadge />
              </>
            )}

            {!user && existingAccountNotice && (
              <div className="mt-6 flex items-center justify-between gap-3 px-4 py-3 rounded-sm bg-[#C0C0C0]/10 border border-[#C0C0C0]/30 text-[#E5E4E2] text-[11px]" data-testid="checkout-existing-account-notice">
                <span className="flex items-center gap-2">
                  <UserCheck className="w-4 h-4 text-[#C0C0C0] shrink-0" />
                  Hai già un account con questa email: accedi per ricevere i tuoi sconti e salvare l'ordine nel tuo profilo.
                </span>
                <button type="button"
                        onClick={() => { setLoginDialogReason("existing_account"); setLoginEmail(form.email.trim()); setLoginDialogOpen(true); }}
                        data-testid="checkout-existing-account-login-btn"
                        className="shrink-0 underline underline-offset-2 hover:text-white font-medium">
                  Accedi
                </button>
              </div>
            )}

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={() => setStep(1)} disabled={loading} data-testid="checkout-back-to-summary"
                      className="sm:w-40 inline-flex items-center justify-center gap-2 border border-white/15 text-[#E5E4E2] px-6 py-4 rounded-sm text-[11px] tracking-[0.24em] uppercase hover:bg-white/5 disabled:opacity-40">
                <ArrowLeft className="w-4 h-4" /> Indietro
              </button>
              <button onClick={pay} disabled={loading} data-testid="checkout-confirm-pay"
                      className="flex-1 inline-flex items-center justify-center gap-3 bg-[#C0C0C0] text-[#060A14] px-8 py-4 rounded-sm text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-white transition-colors disabled:opacity-60">
                {loading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> {paymentMethod === "cod" ? (codNeedsDeposit ? "Avvio pagamento anticipo…" : "Invio ordine…") : "Avvio pagamento…"}</>
                ) : paymentMethod === "cod" ? (
                  codNeedsDeposit ? (
                    <><Lock className="w-4 h-4" /> Paga l'anticipo di {money(depositAmount)} ora</>
                  ) : (
                    <><Wallet className="w-4 h-4" /> Conferma ordine {money(total)}</>
                  )
                ) : (
                  <><Lock className="w-4 h-4" /> Conferma e paga {money(total)}</>
                )}
              </button>
            </div>
          </div>
        )}

        {step === 3 && codOrder && (
          <div data-testid="checkout-cod-confirmed" className="animate-in fade-in zoom-in-95 duration-700 text-center">
            <div className="relative mx-auto w-24 h-24 mb-8">
              <span className="absolute inset-0 rounded-full bg-emerald-500/20 animate-ping" />
              <span className="relative flex items-center justify-center w-24 h-24 rounded-full bg-emerald-500/15 border border-emerald-400/50">
                <CheckCircle2 className="w-12 h-12 text-emerald-400" strokeWidth={1.5} />
              </span>
            </div>
            <h1 className="mt-3 font-[Outfit] text-4xl sm:text-5xl font-light tracking-tight">Ordine confermato!</h1>
            <p className="mt-4 text-[#94A3B8] text-sm leading-relaxed">
              Pagherai <b className="text-[#E5E4E2]">{money(codOrder.total)}</b> in contanti {codOrder.delivery_method === "pickup"
                ? <>al ritiro presso <b className="text-[#E5E4E2]">{codOrder.pickup_point_name || "il punto convenzionato"}</b></>
                : "al corriere alla consegna"}. Riceverai aggiornamenti sulla spedizione via email.
            </p>
            <p className="mt-4 inline-block bg-[var(--lv-surface,#0A1121)] border border-[#C0C0C0]/30 rounded-full px-5 py-2 text-[12px] tracking-[0.2em] uppercase text-[#C0C0C0]" data-testid="cod-order-number">
              Ordine <b className="text-white font-[Outfit]">{codOrder.order_number}</b>
            </p>
            <div className="mt-8 max-w-sm mx-auto text-left bg-[var(--lv-surface,#0A1121)] border border-white/10 rounded-sm px-5 py-5" data-testid="cod-order-summary">
              <dl className="space-y-2 text-sm">
                <div className="flex items-center justify-between text-[#94A3B8]">
                  <dt>Subtotale</dt>
                  <dd className="font-[Outfit] text-[#E5E4E2]" data-testid="cod-summary-subtotal">{money(codOrder.subtotal)}</dd>
                </div>
                {codOrder.discount_amount > 0 && (
                  <div className="flex items-center justify-between text-emerald-300" data-testid="cod-summary-discount-row">
                    <dt className="inline-flex items-center gap-1.5"><Tag className="w-3.5 h-3.5" /> {codOrder.discount_codes?.[0]?.type === "pr_auto" ? `Sconto PR a vita (${pctLabel(codOrder.discount_codes[0].value)}%)` : codOrder.discount_code === "BENVENUTO10" ? `Sconto Benvenuto (${pctLabel(codOrder.discount_codes?.find((d) => d.code === "BENVENUTO10")?.value ?? 10)}%)` : `Sconto${codOrder.discount_code ? ` (${codOrder.discount_code})` : ""}`}</dt>
                    <dd className="font-[Outfit]">−{money(codOrder.discount_amount)}</dd>
                  </div>
                )}
                <div className="flex items-center justify-between text-[#94A3B8]">
                  <dt>{codOrder.delivery_method === "pickup" ? "Ritiro" : "Spedizione"}</dt>
                  <dd className={`font-[Outfit] ${Number(codOrder.shipping_cost) === 0 ? "text-emerald-300" : "text-[#E5E4E2]"}`} data-testid="cod-summary-shipping">
                    {Number(codOrder.shipping_cost) === 0 ? "Gratuita" : money(codOrder.shipping_cost)}
                  </dd>
                </div>
                <div className="flex items-center justify-between pt-3 mt-1 border-t border-white/10">
                  <dt className="text-white tracking-[0.16em] uppercase text-[12px]">Totale (contrassegno)</dt>
                  <dd className="font-[Outfit] text-white text-xl" data-testid="cod-summary-total">{money(codOrder.total)}</dd>
                </div>
              </dl>
            </div>
            <div className="mt-6 flex flex-col items-center gap-4">
              <p className="inline-flex items-center gap-1.5 text-[10px] text-emerald-300 uppercase tracking-[0.2em]">
                <Award className="w-3 h-3" /> Punti Cashback in accredito alla consegna
              </p>
              <Link to="/" data-testid="cod-back-to-shop"
                    className="inline-flex items-center gap-3 bg-[#C0C0C0] text-[#060A14] px-8 py-4 rounded-sm text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-white transition-colors">
                <ShoppingBag className="w-4 h-4" /> Torna allo Shop
              </Link>
            </div>
          </div>
        )}
      </div>

      <Dialog open={mapOpen} onOpenChange={setMapOpen}>
        <DialogContent className="bg-[#0A1121] border-white/10 text-white max-w-2xl" data-testid="checkout-map-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Outfit] font-light text-xl flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#C0C0C0]" /> Comuni con spedizione gratuita
            </DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-[#94A3B8] -mt-2">Clicca su un pin per selezionare automaticamente CAP e provincia.</p>
          <FreeShippingMap
            comuni={mapComuni}
            capFor={mapCapFor}
            onSelect={(pin) => {
              setForm((p) => ({ ...p, postal: pin.cap, province: provinceForCap(pin.cap), city: pin.name }));
              setMapOpen(false);
              toast.success(`${pin.name} selezionata — CAP e provincia compilati`);
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={loginDialogOpen} onOpenChange={setLoginDialogOpen}>
        <DialogContent className="bg-[#0A1121] border-white/10 text-white max-w-sm" data-testid="checkout-login-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Outfit] font-light text-xl flex items-center gap-2">
              <LogIn className="w-4 h-4 text-[#C0C0C0]" /> {loginDialogReason === "expired" ? "Accedi di nuovo" : "Accedi"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-[#94A3B8] -mt-2">
            {loginDialogReason === "existing_account"
              ? "Il carrello e i dati di spedizione già inseriti restano qui — accedi per ricevere i tuoi sconti e salvare l'ordine nel tuo profilo."
              : loginDialogReason === "discount_login_required"
              ? "Questo codice sconto è riservato agli utenti registrati — accedi (o crea un account) e verrà applicato subito. Il carrello e i dati già inseriti restano qui."
              : "Il carrello e i dati di spedizione già inseriti restano qui — accedi per riattivare lo sconto Cliente PR."}
          </p>
          <form onSubmit={handleInlineLogin} className="space-y-3 mt-1">
            {loginError && (
              <div data-testid="checkout-login-error" className="px-3 py-2 rounded-sm bg-red-500/10 border border-red-500/30 text-red-300 text-[12px]">{loginError}</div>
            )}
            <input
              type="email" required autoFocus value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)}
              placeholder="Email" data-testid="checkout-login-email"
              className="w-full bg-[#060A14] border border-white/10 rounded-sm px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#C0C0C0] placeholder:text-[#94A3B8]/60"
            />
            <input
              type="password" required value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)}
              placeholder="Password" data-testid="checkout-login-password"
              className="w-full bg-[#060A14] border border-white/10 rounded-sm px-3 py-2.5 text-white text-sm focus:outline-none focus:border-[#C0C0C0] placeholder:text-[#94A3B8]/60"
            />
            <label className="flex items-center gap-2 cursor-pointer group">
              <input
                type="checkbox" checked={loginRememberMe} onChange={(e) => setLoginRememberMe(e.target.checked)}
                data-testid="checkout-login-remember"
                className="w-4 h-4 accent-[#C0C0C0] cursor-pointer shrink-0"
              />
              <span className="text-[11px] text-[#94A3B8] group-hover:text-[#CBD5E1]">Resta collegato</span>
            </label>
            <button type="submit" disabled={loginBusy} data-testid="checkout-login-submit"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#C0C0C0] text-[#060A14] px-4 py-3 rounded-sm text-[11px] tracking-[0.24em] uppercase font-medium hover:bg-white disabled:opacity-50 transition-colors">
              {loginBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
              {loginBusy ? "Accesso…" : "Accedi"}
            </button>
            {loginDialogReason === "discount_login_required" && (
              <Link to="/register" data-testid="checkout-login-register-link"
                    className="block text-center text-[11px] text-[#94A3B8] hover:text-white underline underline-offset-2">
                Non hai un account? Registrati
              </Link>
            )}
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
